import { DATE_INPUT_CONSTANTS, DateInput, Tooltip } from "@ui-kit"
import { TaskDatePickerProps, TaskPayload } from "../../types/workflow.types"
import { useAppDispatch } from "../../utils/agent.store"
import { WORKFLOW_STATUS } from "../../utils/constants"
import moment from "moment"
import { useCallback, useEffect, useState } from "react"
import { debounce } from "lodash"
import { addToaster, hideLoader, saveTaskDataApi, showLoader } from "@app-store"
import axios from "axios"
import { v4 as uuid } from 'uuid'

const TaskDatePicker: React.FC<TaskDatePickerProps> = ({
  task,
  dateType,
  isDisabled,
  tabIndex,
  min,
  max,
  dueDate,
  startDate,
}) => {
  const dispatch = useAppDispatch()
  const isTaskCompleted = task?.state === WORKFLOW_STATUS?.COMPLETED

  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return ''
    if (typeof date === 'string') return date
    return moment(date).format(DATE_INPUT_CONSTANTS.COMMON_DATE_TIME_FORMAT_2)
  }

  const [validatedDate, setValidatedDate] = useState<string>(() => {
    if (dateType === 'start_date' && task?.start_date) {
      return formatDate(task.start_date)
    } else if (dateType === 'due_date' && task?.due_date) {
      return formatDate(task.due_date)
    }
    return ''
  })
  const [pendingDate, setPendingDate] = useState<string | null>(null)

  const displayDate =
    pendingDate ?? validatedDate
      ? moment(pendingDate ?? validatedDate).format(
          DATE_INPUT_CONSTANTS.COMMON_DATE_TIME_FORMAT_2
        )
      : null

  const saveDate = useCallback(
    debounce(async (newDate: string) => {
      const payload: TaskPayload = {
        id: Number(task?.id),
        [dateType]: newDate,
      }

      try {
        dispatch(showLoader())
        await saveTaskDataApi(task?.id as string, payload)
        setValidatedDate(newDate)
        if (dateType === 'due_date' && dueDate) {
          dueDate.current = newDate
        } else if (dateType === 'start_date' && startDate) {
          startDate.current = newDate
        }
      } catch (err) {
        if (axios.isAxiosError(err)) {
          dispatch(
            addToaster({
              message:
                err?.response?.data?.message ?? WORKFLOW_STATUS.DATE_ERROR_MSG,
              type: 'error',
              id: uuid(),
            })
          )
        }
      } finally {
        setPendingDate(null)
        dispatch(hideLoader())
      }
    }, 800),
    [task?.id, dateType]
  )

  const handleDateChange = (date?: string | null) => {
    setPendingDate(date ?? null)
  }

  useEffect(() => {
    if (pendingDate && !isTaskCompleted) {
      saveDate(
        pendingDate
          ? moment(new Date(pendingDate).toISOString())
              .utc()
              .format('YYYY-MM-DDTHH:mm:ssZ')
          : ''
      )
    }
  }, [pendingDate, saveDate, isTaskCompleted])
  useEffect(() => {
    if (task) {
      if (dateType === 'start_date' && task?.start_date) {
        setValidatedDate(formatDate(task?.start_date))
      } else if (
        dateType === 'due_date' &&
        task?.start_date &&
        task?.due_date
      ) {
        setValidatedDate(formatDate(task?.due_date))
      }
    }
  }, [task])
  return (
    <button
      className="flex items-center bg-inherit font-light text-xs"
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip
        content={displayDate ?? ''}
        className="text-ellipsis max-w-full"
        cssClassName="max-w-full"
      >
        <DateInput
          includeTime={true}
          value={displayDate}
          className={`${
            isTaskCompleted || isDisabled ? 'w-[7rem]' : 'w-[7.3rem]'
          } text-ellipsis`}
          handleChange={handleDateChange}
          isDisabled={isTaskCompleted || isDisabled}
          showIcon={!isTaskCompleted && !isDisabled}
          tabIndex={tabIndex}
          showClear={false}
          min={min as string}
          max={max as string}
        />
      </Tooltip>
    </button>
  )
}
export default TaskDatePicker